import { makeAutoObservable, runInAction } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { Amount } from 'src_common/common/amount/Amount';
import { AccountState } from 'src/domains/players/state/accountState/AccountState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { UsersState } from 'src/domains/players/state/UsersState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { MultiDepositSteps } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/multiDepositJourney/MultiDepositSteps.state';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import { TrpcClient } from 'src/appState/TrpcClient';
import {
    PaysafeBillingInfoSuccess,
    paysafeBillingInfoFieldsValidation,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/helpers/billingInfoFiledsValidation/paysafeBillingInfoFieldsValidation';
import {
    PaymentIframeDataType,
    PaysafeError,
    PaysafeExpired,
    PaysafeInstanceType,
    PaysafeResult,
    PaysafeStage,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/paysafeProvider/paysafeFrame/PaysafeFrameOptions';
import { PaysafePaymentProviderType } from 'src/domains/layouts/config/features/types';
import { createPaymentPaysafe } from 'src_server/trpc/types';
import { CreatePaymentPaysafeType } from 'src_server/trpc/types/createPaymentPaysafe';
import { Logger } from 'src/domains/common/Logger';
import { PaysafePaymentProviderZod } from 'src/domains/common/configFeatures/ConfigFeatures';
import { AmountPrecision } from 'src_common/common/amount/AmountPrecision';

export class PaysafeTabState {
    public isLoading: boolean = true;
    public transactionId: number | undefined = undefined;
    public paymentIframeData: PaymentIframeDataType | null = null;

    private readonly account: AccountState;
    private readonly googleTagManager: GoogleTagManagerState;
    private readonly usersState: UsersState;
    private readonly basicDataModel: BasicDataModel;
    private readonly configComponents: ConfigComponents;
    private readonly trpcClient: TrpcClient;
    private readonly amountPrecision: AmountPrecision;
    public readonly websiteBaseUrl: string;
    public readonly paysafePaymentProviderConfig: PaysafePaymentProviderType;
    private addedDepositTag: boolean = false;

    public constructor(
        public readonly common: Common,
        private readonly amount: string,
        public readonly stepsState: MultiDepositSteps,
        private readonly isSignup: boolean,
        private readonly errorMessagesState: DepositErrorMessagesState
    ) {
        this.account = AccountState.get(common);
        this.googleTagManager = GoogleTagManagerState.get(common);
        this.usersState = UsersState.get(common);
        this.basicDataModel = BasicDataModel.get(common);
        this.configComponents = ConfigComponents.get(common);
        this.trpcClient = common.trpcClient;
        this.amountPrecision = this.configComponents.precision;
        this.websiteBaseUrl = common.websiteBaseUrl;

        this.paysafePaymentProviderConfig = PaysafePaymentProviderZod.parse(
            this.configComponents.config.paymentProviders.applePay
        );

        makeAutoObservable(this);

        this.initializePayment();
    }

    private initializePayment = (): void => {
        setTimeout(() => {
            void this.submitDepositForm();
        }, 0);
    };

    private submitDepositForm = async (): Promise<void> => {
        const amountObj = new Amount(this.amount);
        const oldFormatAmount = this.amountPrecision.valueOldFormat(amountObj);
        const account = this.account.account;

        if (account === null) {
            console.error('submitDepositForm - User is anonymous');
            this.stepsState.redirectToSetAmount();
            return;
        }

        const billingInfo = paysafeBillingInfoFieldsValidation(this.basicDataModel.basicDataReady);
        if (billingInfo?.type !== 'ok') {
            console.error('submitDepositForm - No basic user data');
            this.stepsState.redirectToSetAmount();
            return;
        }

        try {
            const hasRingFencedFunds = this.basicDataModel.basicDataReady?.ringFencedFunds ?? false;
            if (!hasRingFencedFunds) {
                await this.usersState.onChangeRingFencedFlag();
            }

            if (this.isSignup) {
                this.googleTagManager.depositedMoney(oldFormatAmount, true);
                this.googleTagManager.gtmSignUpStepFour(oldFormatAmount);
            } else {
                this.googleTagManager.depositedMoney(oldFormatAmount, false);
            }

            const verifyUserResponse = await this.trpcClient.client.paysafeRouter.verifyUser.mutate({
                merchantCustomerId: billingInfo.data.merchantCustomerId.toString(),
                ...billingInfo.data.customer,
            });

            if (verifyUserResponse.responseStatus === 'error') {
                throw new Error();
            }

            const date = new Date().toUTCString();
            const singleUseTokenResponse =
                await this.trpcClient.client.paysafeRouter.createSingleUseCustomerToken.mutate({
                    externalCustomerId: verifyUserResponse.response.data.externalId,
                    body: {
                        merchantRefNum: `${billingInfo.data.merchantCustomerId.toString()}-${date}`,
                        paymentMethod: ['applePay', 'card'],
                    },
                });

            if (singleUseTokenResponse.responseStatus === 'error') {
                throw new Error();
            }

            runInAction(() => {
                this.paymentIframeData = this.mapIframeData(
                    singleUseTokenResponse.response.data.singleUseCustomerToken,
                    billingInfo.data,
                    oldFormatAmount
                );
                this.isLoading = false;
            });
        } catch (e) {
            this.stepsState.redirectToSetAmount();
            this.errorMessagesState.createErrorMessage('unknown');
            throw e;
        }
    };

    private mapIframeData = (
        singleUseToken: string,
        billingInfo: PaysafeBillingInfoSuccess['data'],
        amount: number
    ): PaymentIframeDataType => {
        const operatorFullName = this.configComponents.config.operatorFullName;

        return {
            customerId: billingInfo.merchantCustomerId,
            data: {
                currency: billingInfo.currency,
                amount: amount,
                singleUseCustomerToken: singleUseToken,
                customer: billingInfo.customer,
                billingAddress: billingInfo.billingAddress,
                merchantRefNum: `${billingInfo.merchantCustomerId}-${singleUseToken}`,
                simulator: 'EXTERNAL',
                environment: this.paysafePaymentProviderConfig.environment,
                buttonColor: '#66cc99',
                imageUrl: 'https://hosted.paysafe.com/checkout/resource/demo-store/images/logo.png',
                companyName: operatorFullName,
                holderName: billingInfo.billingAddress.nickName,
                threeDs: {
                    merchantUrl: this.websiteBaseUrl,
                    deviceChannel: 'BROWSER',
                    messageCategory: 'PAYMENT',
                    transactionIntent: 'GOODS_OR_SERVICE_PURCHASE',
                    authenticationPurpose: 'PAYMENT_TRANSACTION',
                },
                displayPaymentMethods: this.paysafePaymentProviderConfig.applePayEnabled ? ['applePay'] : [],
                paymentMethodDetails: {
                    card: {
                        accountId:
                            billingInfo.currency === 'GBP'
                                ? this.paysafePaymentProviderConfig.cardAccounts.GBP
                                : this.paysafePaymentProviderConfig.cardAccounts.EUR,
                    },
                    applePay: {
                        accountId:
                            billingInfo.currency === 'GBP'
                                ? this.paysafePaymentProviderConfig.cardAccounts.GBP
                                : this.paysafePaymentProviderConfig.cardAccounts.EUR,
                        label: `${operatorFullName}`,
                        requestBillingAddress: true,
                        supportedCountries: ['GB', 'US'],
                        type: 'pay',
                        color: 'black',
                    },
                },
            },
        };
    };

    public paysafeResultCallback =
        (paymentIframeData: PaymentIframeDataType) =>
        async (instance: PaysafeInstanceType | null, error: unknown, result: unknown): Promise<void> => {
            try {
                const date = new Date().toISOString();
                const parsedError = PaysafeError.safeParse(error);
                if (parsedError.success) {
                    console.error('paysafe-iframe-error', parsedError.data);
                    instance?.showFailureScreen(parsedError.data.displayMessage);
                    Logger.captureError(`Paysafe -> SDK error -> ${JSON.stringify(parsedError.data)}`, 'Players');

                    await this.trpcClient.client.paysafeRouter.failedPayment.mutate({
                        amount: this.amountPrecision.newFromOld(paymentIframeData.data.amount).value,
                        currencyCode: paymentIframeData.data.currency,
                        merchantRefNum: paymentIframeData.data.merchantRefNum,
                        error: parsedError.data,
                    });

                    this.stepsState.redirectToFailureView('failWithReceipt', {
                        transactionId: undefined,
                        data: {
                            currency: paymentIframeData.data.currency,
                            amount: paymentIframeData.data.amount,
                            createdAt: date,
                            status: 'failed',
                            orderId: parsedError.data.correlationId,
                        },
                    });
                    return;
                }

                const parsedResult = PaysafeResult.safeParse(result);
                if (parsedResult.success) {
                    const handlePaymentResponse = await this.handlePayment({
                        amount: this.amountPrecision.newFromOld(paymentIframeData.data.amount).value,
                        currencyCode: paymentIframeData.data.currency,
                        description: 'Top-up',
                        keywords: ['Top-up'],
                        paymentHandleToken: parsedResult.data.paymentHandleToken,
                    });

                    if (handlePaymentResponse.responseStatus === 'error') {
                        const error = handlePaymentResponse.data.errors[0];
                        this.handlePaymentError(error, instance, paymentIframeData, date);
                        return;
                    }

                    instance?.showSuccessScreen('Remember to always gamble responsibly');
                    this.stepsState.redirectToFinalView(handlePaymentResponse.response.data.transactionId);

                    if (!this.addedDepositTag) {
                        runInAction(() => {
                            this.addedDepositTag = true;
                        });
                        this.googleTagManager.addDepositTag(this.account.account);
                    }
                    return;
                }

                instance?.showFailureScreen(this.errorMessagesState.errors[0]?.message ?? '');
                Logger.captureError(`Paysafe -> Unknown error`, 'Players');
                this.stepsState.redirectToFailureView('serverIssue');
                return;
            } catch (e) {
                console.error('paysafe-iframe-catch-error', e);
                Logger.captureError(`Paysafe -> Iframe catch error -> ${JSON.stringify(e)}`, 'Players');
                this.stepsState.redirectToFailureView('serverIssue');
                instance?.close();
            }
        };

    public paysafeStateCallback = (stage: unknown, expired: unknown): void => {
        const parsedExpired = PaysafeExpired.safeParse(expired);
        if (parsedExpired.success && parsedExpired.data === true) {
            this.stepsState.redirectToFailureView('contactGeneralIssue');
            return;
        }

        const parsedStage = PaysafeStage.safeParse(stage);

        if (parsedStage.success) {
            if (parsedStage.data === 'PAYMENT_HANDLE_NOT_CREATED') {
                this.stepsState.redirectToFailureView('contactGeneralIssue');
                return;
            }
        }

        return;
    };

    private handlePayment = async (
        requestData: CreatePaymentPaysafeType
    ): Promise<createPaymentPaysafe.TresponseType> => {
        return await this.trpcClient.client.paysafeRouter.createPayment.mutate(requestData);
    };

    private handlePaymentError = (
        error:
            | {
                  code?: string | null;
                  message?: string | null;
                  details?: string[] | null;
                  fieldErrors?: string[] | null;
              }
            | undefined,
        instance: PaysafeInstanceType | null,
        paymentIframeData: PaymentIframeDataType,
        date: string
    ): void => {
        const longMessageErrors = [
            'failed-deposit-restriction',
            'limit-deposit-restriction',
            'limit-reached',
            'backoffice-deposit-restriction',
            'kyc-deposit-restriction',
        ];

        const errorCode = error?.code;
        this.errorMessagesState.createErrorMessage(
            error?.code ?? undefined,
            error?.message ?? undefined,
            error?.fieldErrors ?? []
        );

        if (longMessageErrors.includes(errorCode ?? '')) {
            const errorsCopy = [...this.errorMessagesState.errors];

            instance?.showFailureScreen(
                'There was an error with your payment. More information will be shown in the next step.'
            );
            this.stepsState.redirectToFailureView(
                'failWithReceipt',
                {
                    transactionId: undefined,
                    data: {
                        currency: paymentIframeData.data.currency,
                        amount: paymentIframeData.data.amount,
                        createdAt: date,
                        status: 'failed',
                    },
                },
                errorsCopy
            );
        } else {
            instance?.showFailureScreen(this.errorMessagesState.errors[0]?.message ?? '');
            this.stepsState.redirectToFailureView(
                'failWithReceipt',
                {
                    transactionId: undefined,
                    data: {
                        currency: paymentIframeData.data.currency,
                        amount: paymentIframeData.data.amount,
                        createdAt: date,
                        status: 'failed',
                    },
                },
                this.errorMessagesState.errors
            );
        }
    };
}
